import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ServiceError } from '../api'
import {
  downloadUploadReceipt,
  readUploadReceipt,
  type UploadReceipt as Receipt,
} from '../ingest'
import { moment, size } from '../format'
import { useT } from '../i18n/LocaleProvider'
import { readSnapshot, writeSnapshot } from '../readSnapshots'

interface Loaded {
  receipt: Receipt
  snapshotAt: string | null
}

export function UploadReceipt(): JSX.Element {
  const t = useT()
  const uploadId = useParams().uploadId ?? ''
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [file, setFile] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const receipt = await readUploadReceipt(uploadId)
        if (cancelled) return
        setLoaded({ receipt, snapshotAt: null })
        void writeSnapshot('upload-receipt', uploadId, receipt).catch(() => undefined)
      } catch (error: unknown) {
        const snapshot = await readSnapshot<Receipt>('upload-receipt', uploadId).catch(() => null)
        if (snapshot !== null) {
          if (!cancelled) setLoaded({ receipt: snapshot.value, snapshotAt: snapshot.captured_at })
          return
        }
        if (!cancelled) {
          setFailure(error instanceof ServiceError ? error.message : t('receipt.unread'))
        }
      }
    })().catch((error: unknown) => {
      if (cancelled) return
      setFailure(error instanceof ServiceError ? error.message : t('receipt.unread'))
    })
    return () => {
      cancelled = true
    }
  }, [uploadId])

  useEffect(() => {
    return () => {
      if (file !== null) URL.revokeObjectURL(file)
    }
  }, [file])

  async function prepareDownload(receiptUploadId: string): Promise<void> {
    if (file !== null) URL.revokeObjectURL(file)
    try {
      setFile(URL.createObjectURL(await downloadUploadReceipt(receiptUploadId)))
    } catch (error) {
      setFailure(
        error instanceof ServiceError ? error.message : t('receipt.undownloadable'),
      )
    }
  }

  if (failure !== null) {
    return (
      <main>
        <h2>{t('receipt.unavailable')}</h2>
        <p role="alert">{failure}</p>
      </main>
    )
  }
  if (loaded === null) {
    return (
      <main>
        <p role="status">{t('receipt.reading')}</p>
      </main>
    )
  }
  const { receipt, snapshotAt } = loaded
  return (
    <main>
      <h2>{t('title.uploadReceipt')}</h2>
      {snapshotAt !== null && (
        <p role="note">{t('receipt.offline', { when: moment(snapshotAt) })}</p>
      )}
      <dl>
        <dt>{t('receipt.upload')}</dt>
        <dd className="t-num"><code>{receipt.upload_id}</code></dd>
        <dt>{t('receipt.jobIngest')}</dt>
        <dd className="t-num"><code>{receipt.job_id}</code></dd>
        <dt>{t('receipt.package')}</dt>
        <dd>{receipt.package_name}</dd>
        <dt>{t('receipt.sha256')}</dt>
        <dd className="t-num"><code>{receipt.package_checksum}</code></dd>
        <dt>{t('receipt.objects')}</dt>
        <dd className="t-num">{receipt.objects}</dd>
        <dt>{t('receipt.bytes')}</dt>
        <dd className="t-num">{size(receipt.bytes)}</dd>
        <dt>{t('receipt.author')}</dt>
        <dd>{receipt.author}</dd>
        <dt>{t('receipt.serverTime')}</dt>
        <dd className="t-num">{moment(receipt.issued_at)}</dd>
      </dl>
      <p>
        {file === null ? (
          <button type="button" className="primario" onClick={() => void prepareDownload(receipt.upload_id)}>
            {t('receipt.prepare')}
          </button>
        ) : (
          <a href={file} download={`upload-receipt-${receipt.upload_id}.txt`}>
            {t('receipt.download')}
          </a>
        )}
      </p>
      <p>
        <Link to={`/ingest/${receipt.job_id}`}>{t('upload.openIngest')}</Link>
      </p>
    </main>
  )
}
